// Durable offline store (IndexedDB), scoped per tenant + register. Stores: products, config, sales, sync_state.
const VERSION = 1;
const dbs = {};

export function dbName(tenant, register) { return `neec_offline_${tenant || "main"}_${register || "device"}`; }

export function openDB(tenant, register) {
  const name = dbName(tenant, register);
  if (dbs[name]) return dbs[name];
  dbs[name] = new Promise((resolve, reject) => {
    const req = indexedDB.open(name, VERSION);
    req.onupgradeneeded = () => {
      const d = req.result;
      const p = d.createObjectStore("products", { keyPath: "id" });
      p.createIndex("sku", "sku_nk", { unique: false }); p.createIndex("barcode", "barcode_nk", { unique: false });
      d.createObjectStore("config", { keyPath: "key" });
      const s = d.createObjectStore("sales", { keyPath: "local_id" });
      s.createIndex("sync_status", "sync_status", { unique: false });
      d.createObjectStore("sync_state", { keyPath: "key" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbs[name];
}

const tx = (db, store, mode, fn) => new Promise((res, rej) => {
  const t = db.transaction(store, mode); const st = t.objectStore(store); const out = fn(st);
  t.oncomplete = () => res(out && "result" in out ? out.result : out); t.onerror = () => rej(t.error);
});
const req = (r) => new Promise((res, rej) => { r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); });

export const nk = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Only the fields needed to sell offline. Never cache cost or other tenants' data.
export const snapshotProduct = (p) => ({
  id: p.id, sku: p.sku || "", barcode: p.barcode || "", sku_nk: nk(p.sku), barcode_nk: nk(p.barcode), name: p.name, description: p.description || "",
  price: p.price, tax_status: p.tax_status || "taxable", unit: p.unit || "each", category: p.category || "", brand: p.brand || "",
  product_type: p.product_type || "stock", track_inventory: p.track_inventory !== false, quantity: p.quantity ?? 0, archived: !!p.archived,
  bulk_tiers: p.bulk_tiers || [], bulk_pricing_enabled: !!p.bulk_pricing_enabled, size: p.size || "", model: p.model || "", variant: p.variant || "",
});

const fnv = (str) => { let h = 0x811c9dc5; for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193); } return (h >>> 0).toString(16); };

// Incremental cache refresh: only products whose snapshot changed are written and removed ones deleted (a sale touches 1–3 rows,
// not the whole catalogue). `prev` is an optional {id: hash} map from the last write in this same component mount; when absent
// (e.g. a fresh mount/navigation, where an in-memory ref always resets to null) the existing IndexedDB rows are hashed and used
// as the base instead of clearing the store — this was already necessary for correctness across remounts, and is essential now
// that a response can be `truncated` (B-06): true when `products` is a bounded/paged slice, not the whole catalogue (e.g. POS's
// auto-load cap) — a product's absence from a truncated response means nothing (it may simply sit past the cap), so deletions
// are skipped entirely in that case; only the returned rows are upserted. Returns the new {id: hash} map.
export async function saveCatalogueDelta(db, products, prev, { truncated = false } = {}) {
  let base = prev;
  if (!base) {
    base = {};
    for (const row of await loadCatalogue(db)) base[row.id] = fnv(JSON.stringify(row));
  }
  const next = {}; const changed = [];
  for (const p of products) { const snap = snapshotProduct(p); const h = fnv(JSON.stringify(snap)); next[p.id] = h; if (base[p.id] !== h) changed.push(snap); }
  const removed = truncated ? [] : Object.keys(base).filter((id) => !(id in next));
  const merged = { ...base, ...next };
  if (changed.length || removed.length) {
    await new Promise((res, rej) => {
      const t = db.transaction("products", "readwrite"); const st = t.objectStore("products");
      for (const snap of changed) st.put(snap);
      for (const id of removed) st.delete(id);
      t.oncomplete = res; t.onerror = () => rej(t.error);
    });
  }
  await putConfig(db, "catalogue", { refreshed_at: new Date().toISOString(), count: products.length, truncated });
  return merged;
}

export async function saveCatalogue(db, products) {
  await new Promise((res, rej) => {
    const t = db.transaction("products", "readwrite"); const st = t.objectStore("products"); st.clear();
    for (const p of products) st.put(snapshotProduct(p));
    t.oncomplete = res; t.onerror = () => rej(t.error);
  });
  await putConfig(db, "catalogue", { refreshed_at: new Date().toISOString(), count: products.length });
}
export const loadCatalogue = (db) => new Promise((res, rej) => { const r = db.transaction("products").objectStore("products").getAll(); r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); });
export const putConfig = (db, key, value) => tx(db, "config", "readwrite", (st) => st.put({ key, ...value, saved_at: new Date().toISOString() }));
export const getConfig = (db, key) => new Promise((res, rej) => { const r = db.transaction("config").objectStore("config").get(key); r.onsuccess = () => res(r.result || null); r.onerror = () => rej(r.error); });

export const putSale = (db, sale) => tx(db, "sales", "readwrite", (st) => st.put(sale));
export const getSales = (db) => new Promise((res, rej) => { const r = db.transaction("sales").objectStore("sales").getAll(); r.onsuccess = () => res(r.result.sort((a, b) => a.created_at.localeCompare(b.created_at))); r.onerror = () => rej(r.error); });
export async function pendingSales(db) { return (await getSales(db)).filter((s) => ["pending", "failed", "syncing"].includes(s.sync_status)); }
export async function nextReceiptSeq(db) {
  const cur = (await getConfig(db, "receipt_seq"))?.value || 0;
  await putConfig(db, "receipt_seq", { value: cur + 1 });
  return cur + 1;
}
export const _req = req;
